import { Fragment, useState, useEffect, useCallback } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  XMarkIcon,
  PencilSquareIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import { apiHelpers } from '../../services/api';
import { useCreditCosts } from '../../hooks/useCreditCosts';
import toast from 'react-hot-toast';

const SCHEDULE_OPTIONS = [
  { value: 'manual', label: 'Manual refresh' },
  { value: 'every_12_hours', label: 'Every 12 hours' },
  { value: 'daily', label: 'Once a day' },
  { value: 'weekly', label: 'Once a week' },
  { value: 'monthly', label: 'Once a month' }
];

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' }
];

export default function EditInsightBlockModal({ isOpen, onClose, insightBlock, onUpdated }) {
  const { formatCost } = useCreditCosts();

  const [title, setTitle] = useState('');
  const [prompt, setPrompt] = useState('');
  const [sqlQuery, setSqlQuery] = useState('');
  const [scheduleType, setScheduleType] = useState('manual');
  const [scheduleTime, setScheduleTime] = useState('09:00');
  const [dayOfWeek, setDayOfWeek] = useState(1);
  const [dayOfMonth, setDayOfMonth] = useState(1);

  const [isGenerating, setIsGenerating] = useState(false);
  const [progressText, setProgressText] = useState('');

  useEffect(() => {
    if (insightBlock && isOpen) {
      setTitle(insightBlock.title || '');
      setPrompt(insightBlock.prompt || '');
      setSqlQuery(insightBlock.sqlQuery || '');
      setScheduleType(insightBlock.refreshSchedule?.type || 'manual');
      setScheduleTime(insightBlock.refreshSchedule?.time || '09:00');
      setDayOfWeek(insightBlock.refreshSchedule?.dayOfWeek ?? 1);
      setDayOfMonth(insightBlock.refreshSchedule?.dayOfMonth ?? 1);
      setIsGenerating(false);
      setProgressText('');
    }
  }, [insightBlock, isOpen]);

  const handleClose = () => {
    if (isGenerating) return;
    onClose();
  };

  const buildRefreshSchedule = () => {
    const schedule = { type: scheduleType };
    if (['daily', 'weekly', 'monthly', 'every_12_hours'].includes(scheduleType)) {
      schedule.time = scheduleTime;
    }
    if (scheduleType === 'weekly') schedule.dayOfWeek = dayOfWeek;
    if (scheduleType === 'monthly') schedule.dayOfMonth = dayOfMonth;
    return schedule;
  };

  const titleChanged = title !== (insightBlock?.title || '');
  const promptChanged = prompt !== insightBlock?.prompt;
  const sqlChanged = sqlQuery !== insightBlock?.sqlQuery;
  const needsRegeneration = promptChanged || sqlChanged;

  const scheduleChanged =
    scheduleType !== (insightBlock?.refreshSchedule?.type || 'manual') ||
    scheduleTime !== (insightBlock?.refreshSchedule?.time || '09:00') ||
    dayOfWeek !== (insightBlock?.refreshSchedule?.dayOfWeek ?? 1) ||
    dayOfMonth !== (insightBlock?.refreshSchedule?.dayOfMonth ?? 1);

  const hasChanges = needsRegeneration || scheduleChanged || titleChanged;

  const handleSave = useCallback(async () => {
    if (!hasChanges) {
      onClose();
      return;
    }

    const payload = {
      refreshSchedule: buildRefreshSchedule(),
      title: title.trim()
    };
    if (promptChanged) payload.prompt = prompt.trim();
    if (sqlChanged) payload.sqlQuery = sqlQuery;

    if (!needsRegeneration) {
      // Title/schedule-only change: simple PUT, no SSE
      try {
        const response = await apiHelpers.editInsightBlockSimple(insightBlock._id, payload);
        if (onUpdated) onUpdated(response.data?.insightBlock);
        toast.success('Insight block updated');
        onClose();
      } catch (error) {
        toast.error(error.message || 'Failed to update');
      }
      return;
    }

    // Needs regeneration → SSE streaming
    setIsGenerating(true);
    setProgressText('Starting...');

    try {
      const result = await apiHelpers.editInsightBlockStream(
        insightBlock._id,
        payload,
        (progress) => {
          setProgressText(progress.text || 'Processing...');
          if (progress.details) {
            setProgressText(`${progress.text} ${progress.details}`);
          }
        }
      );

      const updated = result.data?.insightBlock;
      if (updated) {
        if (onUpdated) onUpdated(updated);
        toast.success('Insight block regenerated');
        onClose();
      }
    } catch (error) {
      console.error('Edit insight block error:', error);
      toast.error(error.message || 'Failed to edit insight block');
      setIsGenerating(false);
    }
  }, [insightBlock, title, prompt, sqlQuery, scheduleType, scheduleTime, dayOfWeek, dayOfMonth, hasChanges, needsRegeneration, onUpdated, onClose]);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-3xl transform bg-white dark:bg-surface-900 rounded-2xl shadow-2xl transition-all">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-surface-200 dark:border-surface-700">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                      <PencilSquareIcon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                    </div>
                    <Dialog.Title className="text-lg font-semibold text-surface-900 dark:text-white">
                      Edit Insight Block
                    </Dialog.Title>
                  </div>
                  <button onClick={handleClose} disabled={isGenerating} className="p-2 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800 disabled:opacity-50">
                    <XMarkIcon className="w-5 h-5 text-surface-500" />
                  </button>
                </div>

                {/* Body */}
                <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">
                  {/* Title */}
                  <div>
                    <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1.5">
                      Title
                    </label>
                    <input
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      disabled={isGenerating}
                      maxLength={200}
                      placeholder="e.g. Top Procedures by Revenue"
                      className="w-full px-4 py-2.5 rounded-xl border border-surface-300 dark:border-surface-600 bg-white dark:bg-surface-800 text-surface-900 dark:text-white placeholder-surface-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50"
                    />
                    <p className="mt-1 text-xs text-surface-400">Displayed in the insight block header. Leave blank to use the AI-generated title.</p>
                  </div>

                  {/* Prompt */}
                  <div>
                    <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1.5">
                      Prompt
                    </label>
                    <textarea
                      value={prompt}
                      onChange={(e) => setPrompt(e.target.value)}
                      disabled={isGenerating}
                      rows={3}
                      className="w-full px-4 py-3 rounded-xl border border-surface-300 dark:border-surface-600 bg-white dark:bg-surface-800 text-surface-900 dark:text-white placeholder-surface-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none disabled:opacity-50"
                    />
                    {promptChanged && (
                      <p className="mt-1 text-xs text-amber-500">Prompt changed -- will regenerate with AI {formatCost('insight_block_create')}</p>
                    )}
                  </div>

                  {/* SQL Query */}
                  <div>
                    <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1.5">
                      SQL Query
                    </label>
                    <textarea
                      value={sqlQuery}
                      onChange={(e) => setSqlQuery(e.target.value)}
                      disabled={isGenerating}
                      rows={6}
                      spellCheck={false}
                      className="w-full px-4 py-3 rounded-xl border border-surface-300 dark:border-surface-600 bg-white dark:bg-surface-800 text-surface-900 dark:text-white font-mono text-sm placeholder-surface-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-y disabled:opacity-50"
                      placeholder="SELECT * FROM ${dataset}.table_name"
                    />
                    {sqlChanged && !promptChanged && (
                      <p className="mt-1 text-xs text-amber-500">SQL changed -- will re-run and re-analyze {formatCost('insight_block_create')}</p>
                    )}
                  </div>

                  {/* Refresh Schedule */}
                  <div>
                    <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1.5">
                      Refresh Schedule
                    </label>
                    <div className="flex flex-wrap gap-3">
                      <select
                        value={scheduleType}
                        onChange={(e) => setScheduleType(e.target.value)}
                        disabled={isGenerating}
                        className="flex-1 min-w-[180px] px-3 py-2 rounded-lg border border-surface-300 dark:border-surface-600 bg-white dark:bg-surface-800 text-surface-900 dark:text-white text-sm disabled:opacity-50"
                      >
                        {SCHEDULE_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>

                      {['daily', 'weekly', 'monthly', 'every_12_hours'].includes(scheduleType) && (
                        <input
                          type="time"
                          value={scheduleTime}
                          onChange={(e) => setScheduleTime(e.target.value)}
                          disabled={isGenerating}
                          className="px-3 py-2 rounded-lg border border-surface-300 dark:border-surface-600 bg-white dark:bg-surface-800 text-surface-900 dark:text-white text-sm disabled:opacity-50"
                        />
                      )}

                      {scheduleType === 'weekly' && (
                        <select
                          value={dayOfWeek}
                          onChange={(e) => setDayOfWeek(Number(e.target.value))}
                          disabled={isGenerating}
                          className="px-3 py-2 rounded-lg border border-surface-300 dark:border-surface-600 bg-white dark:bg-surface-800 text-surface-900 dark:text-white text-sm disabled:opacity-50"
                        >
                          {DAYS_OF_WEEK.map(d => (
                            <option key={d.value} value={d.value}>{d.label}</option>
                          ))}
                        </select>
                      )}

                      {scheduleType === 'monthly' && (
                        <select
                          value={dayOfMonth}
                          onChange={(e) => setDayOfMonth(Number(e.target.value))}
                          disabled={isGenerating}
                          className="px-3 py-2 rounded-lg border border-surface-300 dark:border-surface-600 bg-white dark:bg-surface-800 text-surface-900 dark:text-white text-sm disabled:opacity-50"
                        >
                          {Array.from({ length: 28 }, (_, i) => i + 1).map(d => (
                            <option key={d} value={d}>Day {d}</option>
                          ))}
                        </select>
                      )}
                    </div>
                  </div>

                  {/* Progress */}
                  {isGenerating && (
                    <div className="flex items-center gap-3 p-4 bg-primary-50 dark:bg-primary-900/20 rounded-xl">
                      <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin flex-shrink-0" />
                      <span className="text-sm text-primary-700 dark:text-primary-300">{progressText}</span>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between px-6 py-4 border-t border-surface-200 dark:border-surface-700">
                  <span className="text-xs text-surface-400">
                    {needsRegeneration
                      ? `Regeneration cost: ${formatCost('insight_block_create', { showParens: false }) || '20 credits'}`
                      : scheduleChanged && !titleChanged
                        ? `Schedule/title update (free) · Each refresh costs ${formatCost('insight_block_refresh', { showParens: false }) || '5 credits'}`
                        : titleChanged && !scheduleChanged
                          ? 'Title update (free)'
                          : ''}
                  </span>
                  <div className="flex gap-3">
                    <button
                      onClick={handleClose}
                      disabled={isGenerating}
                      className="px-4 py-2 text-sm font-medium text-surface-700 dark:text-surface-300 bg-surface-100 dark:bg-surface-800 rounded-lg hover:bg-surface-200 dark:hover:bg-surface-700 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={!hasChanges || isGenerating}
                      className="btn-primary disabled:opacity-50"
                    >
                      {isGenerating ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                          Regenerating...
                        </>
                      ) : needsRegeneration ? (
                        <>
                          <SparklesIcon className="w-4 h-4 mr-2" />
                          Regenerate
                        </>
                      ) : (
                        'Save Changes'
                      )}
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
